import { Role } from 'auth';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { APPLY_REGISTER_ID } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import {
  USER_ENTITIES_QUERY_KEY,
  useCreateEntityMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { mapEntityTypeToTexts } from '@/modules/entity/static/entity-types.ts';
import { Button, TooltipNew } from 'ui';
import { InfoCircleIcon } from 'ui/icons';
import { CountryCodeEnum, CreateEntityRequest, EntityType } from 'api';

const entityOptions = Object.values(EntityType).map(type => ({
  type,
  name: mapEntityTypeToTexts[type].name,
  description: mapEntityTypeToTexts[type].description,
}));

const CreateEntityPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const auth = useAuth();

  const state = location.state;

  const { data } = useEntityItemUncachedQuery(
    state?.entityId ? +state.entityId : -1,
    !state?.entityId || state.entityId === 0 || isNaN(+state.entityId),
  );

  const apply_register_id = params.get(APPLY_REGISTER_ID);
  const [entityType, setEntityType] = useState(EntityType.Individual);

  const { mutateAsync: createEntity, isLoading } = useCreateEntityMutation({
    onSuccess: data => {
      navigate(
        {
          pathname: entityPaths.static.updateEntity + `/${data.entity.id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
    },
  });

  const { mutateAsync: updateEntity, isLoading: isLoadingUpdate } = useUpdateEntityMutation({
    onSuccess: data => {
      navigate(
        {
          pathname: entityPaths.static.updateEntity + `/${data.entity.id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
      queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
    },
  });

  const handleCreateEntity = async () => {
    if (data?.entity?.entity_id) {
      await updateEntity({
        ...data?.entity, // ToDo: After changed type need delete all prev fields
        type: entityType,
        country: state?.country,
      });
    } else {
      await createEntity({
        type: entityType,
        individual_entity_details: { country: 'AUS' },
        notifications_enabled: true,
        send_information_about_other_investments: true,
        country: state?.country,
        apply_register_id: Number(apply_register_id),
        // ...(apply_register_id ? { apply_register_id: Number(apply_register_id) } : {}),
      } as CreateEntityRequest);
      await auth.signinSilent();
    }
  };

  const handleBack = () => {
    navigate(
      { pathname: entityPaths.static.abnLookupEntity, search: params.toString() },
      { state: { entityId: state?.entityId } },
    );
  };

  const mainAction = useMemo(
    () => ({
      name: 'Back to entities list',
      onClick: () =>
        navigate(
          { pathname: entityPaths.static.entityList, search: params.toString() },
          {
            replace: false,
          },
        ),
    }),
    [navigate, params],
  );

  return (
    <SignUpPagesWrapper
      backAction={{ onClick: handleBack }}
      mainAction={mainAction}
      className="justify-start"
    >
      <div className="flex max-w-[660px] w-full sm:h-full px-6">
        <div className="w-full flex flex-col justify-center items-end">
          <p className="sm:text-[48px] sm:leading-[56px] text-[38px] leading-[46px] mb-10 font-semibold font-[RecifeDisplay]">
            What entity type are you investing with?
          </p>
          <div className="w-full flex flex-col align-middle justify-center space-y-6">
            {entityOptions.map(({ type, name, description }) => {
              const disabled = state?.country !== CountryCodeEnum.AUS && type === EntityType.Smsf;
              return (
                <Button
                  onClick={() => setEntityType(type)}
                  key={'EntityType_' + type}
                  variant="secondary"
                  className={classNames('flex justify-between relative', {
                    'border-[#486a7b]': entityType === type,
                    'disabled:bg-white disabled:cursor-not-allowed disabled:text-gray-600 disabled:border-gray-200':
                      disabled,
                  })}
                  textAlign="left"
                  disabled={disabled}
                >
                  <span>{name}</span>
                  <TooltipNew
                    text={`${description}${disabled ? ' Available in Australia only' : ''}`}
                    place="left"
                    variant="light"
                    classNameTooltip="border"
                    className="cursor-help"
                  >
                    <img alt="info" src={InfoCircleIcon} />
                  </TooltipNew>
                </Button>
              );
            })}
          </div>
          <div className={'w-full mt-10 flex items-center justify-between'}>
            {/*<Button onClick={handleBack} className="w-[93px] self-end rounded-lg">*/}
            {/*  Back*/}
            {/*</Button>*/}
            <Button
              disabled={isLoading || isLoadingUpdate}
              onClick={handleCreateEntity}
              className="w-full rounded-lg"
              type="submit"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(CreateEntityPage, Role.SR_INVESTOR);
